import React, { useContext, useMemo } from 'react';
import { Price } from 'src/components/Helpers/Price';
import { BasketContext, BasketItemType } from 'src/components/Basket/BasketContext';
import { CartTotals } from 'src/core/state/cart';
import { QuoteDataTotalSegmentInterface } from 'swagger/ts/Definitions';
import { SkeletonLine } from 'src/components/Helpers/SkeletonLine';
import { Tooltip } from 'react-tooltip';
import { CLOSE_TOOLTIP_TIME } from 'src/constants/basket';
import { Inventory } from 'src/components/Basket/Inventory';
import { useSelector } from 'react-redux';
import { useInventory } from 'src/components/Basket/hooks/useInventory';

import classes from './BasketSummary.scss';

type Props = {
    showShipping?: boolean;
};

const TotalLineLabel = () => (
    <span>
        Your total <small>(inc. VAT)</small>
    </span>
);

const ExVatLabel = () => (
    <span>
        Total <small>(ex. VAT)</small>
    </span>
);

export const SummarySkeleton: React.FC = () => (
    <div>
        <SkeletonLine />
        <SkeletonLine />
        <SkeletonLine width={10} />
    </div>
);

export const SetupFeeLabel = () => (
    <span className={classes.labelContainer}>
        One Time Setup Fees
        <label className={classes.infoIconLabel}>
            <div
                className={classes.infoIconContainer}
                data-tooltip-id="infoLabel"
                data-tooltip-content="You won't pay this fee again for future orders with the same logo"
            >
                <img src={require('../../../static/icons/info.svg')} alt="Info" data-img="info-img" />
            </div>
            <button style={{ display: 'none' }} type="button"></button>
            <Tooltip id="infoLabel" variant="light" openOnClick delayHide={5000} />
        </label>
        <label className={classes.mobileInfoIconLabel}>
            <div
                className={classes.infoIconContainer}
                data-tooltip-id="mobileInfoLabel"
                data-tooltip-content="You won't pay this fee again for future orders with the same logo"
            >
                <img src={require('../../../static/icons/info.svg')} alt="Info" data-img="info-img" />
            </div>
            <button type="button"></button>
            <Tooltip id="mobileInfoLabel" variant="light" openOnClick delayHide={CLOSE_TOOLTIP_TIME} />
        </label>
    </span>
);

export const BasketTotalsSegments: React.FC<Props> = React.memo(() => {
    const { isPending, totals, sumTotalCost, setupCost, basketItems } = useContext(BasketContext);

    const calculatedItems: SummaryItem[] = useMemo(() => {
        if (isPending || !totals) {
            return [];
        }

        return getTotalsForCart(totals, setupCost, sumTotalCost, basketItems);
    }, [basketItems, isPending, totals, setupCost, sumTotalCost]);

    /**
     * Same fix as `BasketItem.tsx`: react-redux 9 + React 19 compares
     * selector output with `Object.is`, so wrapping the cart slice in
     * a fresh object literal every render caused an infinite re-render
     * loop. Return the stable reducer reference directly.
     */
    const cart = useSelector((state: any) => state.cart);

    const inventory = useInventory(cart);

    return (
        <div className={classes.summaryTable}>
            {calculatedItems.length <= 0 ? <SummarySkeleton /> : <Inventory inventory={inventory} />}
        </div>
    );
});

export type SummaryItem = { label: React.ReactNode; price: React.ReactNode; bold?: boolean; id: string };

/**
 * @param totals
 * @param isPending
 */
export function getTotalsForCart(
    totals: CartTotals,
    setupCost: number,
    sumTotalCost: number,
    items: BasketItemType[],
    total_segments?: QuoteDataTotalSegmentInterface[],
): SummaryItem[] {
    const out: SummaryItem[] = [];

    const customisationCostsSummary = items
        .map((item) => item.customisationsCost)
        .reduce((previousValue, currentValue) => previousValue + currentValue, 0);

    const productCosts = totals.subtotal_excluding_tax - setupCost - customisationCostsSummary;
    const totalExVat = productCosts + customisationCostsSummary + setupCost;
    const shipping = total_segments?.filter((segment) => segment.code === 'shipping')[0].value;

    const grandTotal = shipping
        ? totalExVat + totals.applied_taxes[0]?.amount + shipping
        : totalExVat + totals.applied_taxes[0]?.amount;

    out.push({
        label: 'Product Costs',
        id: 'ProductCosts',
        price: <Price priceIncl={productCosts} price={productCosts} />,
    });
    {
        sumTotalCost > 0 &&
            out.push({
                label: 'Costs To Add Logo',
                id: 'ApplicationCosts',
                price: <Price priceIncl={customisationCostsSummary} price={customisationCostsSummary} />,
            });
    }
    {
        setupCost > 0 &&
            out.push({
                label: <SetupFeeLabel />,
                id: 'Fees',
                price: <Price priceIncl={setupCost} price={setupCost} />,
            });
    }

    // DISCOUNTS
    totals.discounts.length &&
        totals.discounts.map((x) => {
            out.push({
                label: x.label,
                id: x.label,
                price: <Price price={x.amount} priceIncl={x.amount} />,
            });
        });

    // EX VAT
    {
        !shipping &&
            out.push({
                id: 'total-ex-vat',
                label: <ExVatLabel />,
                price: <Price priceIncl={totalExVat} price={totalExVat} />,
            });
    }

    //SHIPPING
    {
        shipping &&
            out.push({
                id: 'shipping',
                label: 'Delivery',
                price: shipping === 0 ? <span>FREE</span> : <Price priceIncl={shipping} price={shipping} />,
            });
    }

    // VAT
    out.push({
        label: 'VAT',
        id: 'VAT',
        price: <Price priceIncl={totals.applied_taxes[0]?.amount || 0} price={totals.applied_taxes[0]?.amount || 0} />,
    });

    // GRAND TOTAL
    out.push({
        id: 'grand-total',
        label: <TotalLineLabel />,
        price: <Price priceIncl={grandTotal} price={grandTotal} />,
        bold: true,
    });

    return out;
}
